import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";

import { rankLabelsBSchools } from "../../../../constants";
import { useDeviceType } from "../../../customHooks/useDeviceType";
import ArrowDashIcon from "../../../atoms/Arrow-DashIcon";

import "./table-view.m.css";

export const TableView = ({ top100BillionairesList, visibleItemsForList, columns, currentView, subcolumns }) => {
  const visibleItems = ["city", "zone"].includes(currentView) ? top100BillionairesList.length : visibleItemsForList;

  const isMobile = useDeviceType() !== "desktop";
  const scrollContainerRef = useRef(null);
  const tableRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [isTableInViewport, setIsTableInViewport] = useState(false);
  const [showArrows, setShowArrows] = useState(false);

  const firstHeader = () => {
    return rankLabelsBSchools[currentView] || "";
  };

  const scrollTable = (direction) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrollAmount = 350;
    container.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  const updateScrollState = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    setCanScrollLeft(container.scrollLeft > 0);
    if (container.scrollWidth) {
      setCanScrollRight(!(container.scrollLeft + container.clientWidth + 1 > container.scrollWidth));
    }
  };

  const checkTableVisibility = () => {
    const table = tableRef.current;
    if (!table) return;

    const rect = table.getBoundingClientRect();
    const isVisible =
      rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;
    setIsTableInViewport(isVisible);

    // Check if 50% of the table is visible
    const tableHeight = rect.height;
    const visibleHeight = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);
    const is50PercentVisible = visibleHeight >= tableHeight * 0.5;

    // Show arrows when table is visible and 50% or more is in viewport
    setShowArrows((prevShowArrows) => {
      if (isVisible && is50PercentVisible) {
        return true;
      } else if (isVisible && prevShowArrows && rect.top < window.innerHeight * 0.5) {
        return true;
      } else if (isVisible && rect.top < window.innerHeight * 0.3) {
        return true;
      } else {
        return false;
      }
    });
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    updateScrollState();
    checkTableVisibility();

    container.addEventListener("scroll", updateScrollState);
    window.addEventListener("resize", () => {
      updateScrollState();
      checkTableVisibility();
    });
    window.addEventListener("scroll", () => {
      checkTableVisibility();
      if (showArrows) {
        setCanScrollLeft((prev) => prev);
      }
    });

    return () => {
      container.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
      window.removeEventListener("scroll", checkTableVisibility);
    };
  }, [isTableInViewport, showArrows]);

  // Update visibility when visibleItemsForList changes (load more)
  useEffect(() => {
    setTimeout(() => {
      updateScrollState();
      checkTableVisibility();
    }, 200);

    setTimeout(() => {
      updateScrollState();
      checkTableVisibility();
    }, 500);
  }, [visibleItemsForList]);

  // Calculate arrow position based on 50% of viewport height
  const getArrowStyle = (isLeft) => {
    if (!showArrows) return { display: "none" };

    // Calculate 50% of the viewport height
    const viewportMiddle = window.innerHeight / 2;

    const arrowTop = viewportMiddle - 13; // 13px is half of the arrow height (26px)

    return {
      position: "fixed",
      top: `${arrowTop}px`,
      [isLeft ? "left" : "right"]: "20px",
      zIndex: 100,
    };
  };

  return (
    <div styleName="tableWrapper">
      <div styleName="tableContainer" ref={scrollContainerRef}>
        <table styleName="table" ref={tableRef}>
          <thead>
            <tr>
              {columns.map((column, index) =>
                column.name ? (
                  <th key={index} styleName={`table-header ${column.value}`} colSpan={column.colSpan}>
                    {index === 0 ? firstHeader() : column.name}
                  </th>
                ) : (
                  ""
                )
              )}
            </tr>
            <tr>
              {subcolumns.map((column, index) => (
                <th key={index} styleName={`table-header ${column.value}`}>
                  {column.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {top100BillionairesList.slice(0, visibleItems).map((entityData, index) => (
              <tr key={index}>
                {columns.map((c, key) => (
                  <td key={`${index}-${key}`} styleName={`cell ${c.value}`}>
                    {entityData[c.value] || "0"}
                    {/* <span>{c.value === "name" && currentView === "overall" ? entityData.city : ""}</span> */}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Scroll Arrows - positioned at 50% of table height */}
      {!isMobile && (
        <>
          {canScrollLeft && (
            <button
              styleName="scroll-arrow left-arrow"
              onClick={() => scrollTable("left")}
              aria-label="Scroll left"
              style={getArrowStyle(true)}
            >
              <ArrowDashIcon height="26" width="26" viewBox="0 0 26 26" />
            </button>
          )}
          {canScrollRight && (
            <button
              styleName="scroll-arrow right-arrow"
              onClick={() => scrollTable("right")}
              aria-label="Scroll right"
              style={getArrowStyle(false)}
            >
              <ArrowDashIcon height="26" width="26" viewBox="0 0 26 26" />
            </button>
          )}
        </>
      )}
    </div>
  );
};

TableView.propTypes = {
  top100BillionairesList: PropTypes.arrayOf(PropTypes.object).isRequired,
  visibleItemsForList: PropTypes.number.isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.string.isRequired,
      colSpan: PropTypes.number,
    })
  ).isRequired,
  currentView: PropTypes.string.isRequired,
  subcolumns: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.string.isRequired,
    })
  ).isRequired,
};
